import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {BehaviorSubject, map, Observable, tap} from "rxjs";
import {
  ActionResult,
  ActionResultList,
  Competitor, CompetitorFilter,
  CompetitorsCategories,
  FetchRaceByIdRequest, PagedFilter,
} from "@model";
import {LoaderService} from "./loader.service";
import {EnvironmentService} from "./environment.service";

@Injectable({providedIn: 'root'})
export class CompetitorService {

  static readonly START_BLOCK_LIMIT: number = 10;
  static readonly START_BLOCK_MISSING: number = 11;
  static readonly EVENT_ASSIGNED: number = 12;
  static readonly RACE_ASSIGNED: number = 13;

  private static _filteredRaceCompetitors: BehaviorSubject<Competitor[] | undefined> = new BehaviorSubject<Competitor[] | undefined>(undefined);
  private static _raceCompetitors: BehaviorSubject<Competitor[] | undefined> = new BehaviorSubject<Competitor[] | undefined>(undefined);
  private static _competitorsCategories: BehaviorSubject<CompetitorsCategories | undefined> = new BehaviorSubject<CompetitorsCategories | undefined>(undefined);


  get filteredRaceCompetitors(): BehaviorSubject<Competitor[] | undefined> {
    return CompetitorService._filteredRaceCompetitors;
  }

  get raceCompetitors(): BehaviorSubject<Competitor[] | undefined> {
    return CompetitorService._raceCompetitors;
  }

  get competitorsCategories(): BehaviorSubject<CompetitorsCategories | undefined> {
    return CompetitorService._competitorsCategories;
  }

  constructor(private http: HttpClient, private loaderService: LoaderService, private environmentService: EnvironmentService) {
  }

  getCompetitorsCategories(): void {
    if (this.raceCompetitors.value) {
      this.raceCompetitors.pipe(map((competitors: Competitor[] | undefined) => {
        let competitorsCategories: CompetitorsCategories = new CompetitorsCategories();
        competitors?.forEach((competitor: Competitor) => {
          if (competitor.categoryLabel) {
            competitorsCategories.category.add(competitor.categoryLabel);
          }
          if (competitor.sex) {
            competitorsCategories.genders.add(competitor.sex);
          }
          if (competitor.grouping) {
            competitorsCategories.grouping.add(competitor.grouping);
          }
          if (competitor.club) {
            competitorsCategories.club.add(competitor.club);
          }
        })
        return competitorsCategories;
      })).subscribe((result: CompetitorsCategories) => this.competitorsCategories.next(result));
    }
  }

  updateCompetitor(competitor: Competitor, onSuccess?: (competitor: Competitor) => void, onError?: (errorCode?: number) => void): void {
    this.loaderService.startLoading();
    this.http.post<ActionResult<Competitor>>(this.environmentService.getApiUrl() + "/api/competitors/update", competitor)
      .subscribe((actionResult: ActionResult<Competitor>) => {
        this.loaderService.dismissLoading();
        if (actionResult.result) {
          if (onSuccess) {
            onSuccess(actionResult.result);
          }
        } else {
          if (onError) {
            onError(actionResult.resultCode);
          }
        }
      });
  }

  manageCompetitor(competitor: Competitor, onSuccess?: (competitor: Competitor) => void, onError?: (errorCode?: number) => void): void {
    this.loaderService.startLoading();
    this.http.post<ActionResult<Competitor>>(this.environmentService.getApiUrl() + "/api/competitors/manage", competitor)
      .subscribe((actionResult: ActionResult<Competitor>) => {
        this.loaderService.dismissLoading();
        if (actionResult.result) {
          if (onSuccess) {
            onSuccess(actionResult.result);
          }
        } else {
          if (onError) {
            onError(actionResult.resultCode);
          }
        }
      });
  }

  getCompetitors(filter: PagedFilter<CompetitorFilter>): Observable<Competitor[]> {
    return this.http.post<ActionResultList<Competitor[], number>>(this.environmentService.getApiUrl() + "/api/public/races/getcompetitorforrace2", filter).pipe(map((value: ActionResultList<Competitor[], number>) => {
      if (value && value.result && value.result.length != 0) {
        return value.result;
      } else {
        return [];
      }
    }));
  }

  getCompetitorsForRace(raceRequest: FetchRaceByIdRequest, hideLoading?: boolean, onStartFetch?: () => void, onEndFetch?: () => void): Observable<Competitor[]> {
    if (onStartFetch) {
      onStartFetch();
    }
    if (!hideLoading) {
      this.loaderService.startLoading();
    }
    return this.http.post<ActionResultList<Competitor[], number>>(this.environmentService.getApiUrl() + "/api/public/races/getcompetitorforrace", raceRequest).pipe(tap(() => {
      if (onEndFetch) {
        onEndFetch();
      }
      if (!hideLoading) {
        this.loaderService.dismissLoading();
      }
    }), map((value: ActionResult<Competitor[]>) => {
      if (value && value.result && value.result.length) {
        return value.result;
      } else {
        return [];
      }
    }));
  }
}
